import React from 'react';
import { cn } from '../utils';

export interface RadioProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  radioSize?: 'xs' | 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary' | 'accent' | 'success' | 'warning' | 'info' | 'error';
}

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  ({ label, radioSize = 'md', color, className, ...props }, ref) => {
    const radioClasses = cn(
      'radio',
      radioSize === 'xs' && 'radio-xs',
      radioSize === 'sm' && 'radio-sm',
      radioSize === 'lg' && 'radio-lg',
      color === 'primary' && 'radio-primary',
      color === 'secondary' && 'radio-secondary',
      color === 'accent' && 'radio-accent',
      color === 'success' && 'radio-success',
      color === 'warning' && 'radio-warning',
      color === 'info' && 'radio-info',
      color === 'error' && 'radio-error',
      className
    );

    if (label) {
      return (
        <label className="label cursor-pointer">
          <input ref={ref} type="radio" className={radioClasses} {...props} />
          <span className="label-text ml-2">{label}</span>
        </label>
      );
    }

    return <input ref={ref} type="radio" className={radioClasses} {...props} />;
  }
);

Radio.displayName = 'Radio';

export interface RadioGroupProps {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  value,
  onChange,
  children,
  className,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  return (
    <div className={cn('space-y-2', className)}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === Radio) {
          return React.cloneElement(child as React.ReactElement<RadioProps>, {
            name,
            checked: value === child.props.value,
            onChange: handleChange,
          });
        }
        return child;
      })}
    </div>
  );
};
